import React, { forwardRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../../utils/cn';

/**
 * GlassSelect - Executive design system select component
 *
 * A premium select dropdown with glass morphism effects and smooth transitions.
 * Matches the visual style of other Glass components in the design system.
 *
 * @example
 * ```tsx
 * <GlassSelect
 *   value={selectedValue}
 *   onChange={(value) => setSelectedValue(value)}
 *   options={[
 *     { value: 'option1', label: 'Option 1' },
 *     { value: 'option2', label: 'Option 2' },
 *   ]}
 *   placeholder="Select an option"
 * />
 * ```
 */

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface GlassSelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'size'> {
  /** Current selected value */
  value: string;
  /** Callback when value changes */
  onChange: (value: string) => void;
  /** Available options */
  options: SelectOption[];
  /** Optional label */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Error message */
  error?: string;
  /** Helper text below the select */
  helperText?: string;
  /** Select size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Expand to full width */
  fullWidth?: boolean;
  /** Additional CSS classes */
  className?: string;
}

const sizeClasses = {
  sm: 'py-1.5 pl-3 pr-8 text-sm',
  md: 'py-2 pl-4 pr-10 text-base',
  lg: 'py-2.5 pl-5 pr-12 text-lg',
};

const iconSizeClasses = {
  sm: 'w-3.5 h-3.5 right-2',
  md: 'w-4 h-4 right-3',
  lg: 'w-5 h-5 right-3.5',
};

export const GlassSelect = forwardRef<HTMLSelectElement, GlassSelectProps>(
  (
    {
      value,
      onChange,
      options,
      label,
      placeholder,
      error,
      helperText,
      size = 'md',
      fullWidth = false,
      className,
      disabled,
      id,
      ...props
    },
    ref
  ) => {
    const selectId = id || `glass-select-${Math.random().toString(36).substr(2, 9)}`;

    const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
      onChange(event.target.value);
    };

    const selectClasses = cn(
      // Base styles
      'appearance-none cursor-pointer',
      'rounded-xl',
      'border border-border-primary',
      'bg-[var(--input-bg)] backdrop-blur-sm text-[var(--input-text)]',
      'text-foreground',
      'transition-all duration-200',
      // Focus styles
      'focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/40 focus:border-[var(--accent)]',
      // Hover styles
      'hover:border-border-primary hover:bg-[var(--bg-hover)]',
      // Size
      sizeClasses[size],
      // Error state
      error && 'border-status-error/50 focus:ring-status-error/40 focus:border-status-error',
      // Disabled state
      disabled && 'opacity-50 cursor-not-allowed bg-bg-tertiary dark:bg-bg-secondary',
      // Width
      'w-full',
      // Custom classes
      className
    );

    return (
      <div className={cn('relative', fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-muted-foreground mb-1.5"
          >
            {label}
          </label>
        )}

        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            value={value}
            onChange={handleChange}
            disabled={disabled}
            className={selectClasses}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>

          <ChevronDown
            className={cn(
              'absolute top-1/2 -translate-y-1/2 pointer-events-none',
              'text-muted-foreground/60',
              iconSizeClasses[size]
            )}
          />
        </div>

        {error && (
          <p className="mt-1.5 text-sm text-rose-600">{error}</p>
        )}

        {helperText && !error && (
          <p className="mt-1.5 text-sm text-muted-foreground/60">{helperText}</p>
        )}
      </div>
    );
  }
);

GlassSelect.displayName = 'GlassSelect';
